import type { ReactNode } from 'react';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'accent' | 'dark' | 'purple';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-[var(--asi-bg-tertiary)] text-[var(--asi-text)]',
  success: 'bg-[var(--asi-success-light)] text-[var(--asi-success)]',
  warning: 'bg-[var(--asi-warning-light)] text-[var(--asi-warning)]',
  error: 'bg-[var(--asi-error-light)] text-[var(--asi-error)]',
  info: 'bg-[var(--asi-info-light)] text-[var(--asi-info)]',
  accent: 'bg-[var(--asi-accent-light)] text-[var(--asi-text)]',
  dark: 'bg-[var(--asi-primary)] text-[var(--asi-text-light)]',
  purple: 'bg-[#F3E8FF] text-[#7C3AED]',
};

const dotColors: Record<BadgeVariant, string> = {
  default: 'bg-[var(--asi-text-secondary)]',
  success: 'bg-[var(--asi-success)]',
  warning: 'bg-[var(--asi-warning)]',
  error: 'bg-[var(--asi-error)]',
  info: 'bg-[var(--asi-info)]',
  accent: 'bg-[var(--asi-accent)]',
  dark: 'bg-[var(--asi-text-light)]',
  purple: 'bg-[#7C3AED]',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-[10px]',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  className = '',
}: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium tracking-tight ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
    >
      {dot && (
        <span
          className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]}`}
        />
      )}
      {children}
    </span>
  );
}

// Helper function to get badge variant from status
export function getStatusVariant(status: string): BadgeVariant {
  const statusMap: Record<string, BadgeVariant> = {
    // Lead statuses
    new: 'info',
    contacted: 'warning',
    qualified: 'accent',
    converted: 'success',
    lost: 'error',
    // Deal stages
    discovery: 'info',
    qualification: 'accent',
    proposal: 'accent',
    negotiation: 'warning',
    won: 'success',
    // Task statuses
    pending: 'warning',
    in_progress: 'info',
    completed: 'success',
    cancelled: 'error',
    // Campaign statuses
    draft: 'default',
    active: 'success',
    paused: 'warning',
    // Content statuses
    idea: 'default',
    review: 'warning',
    scheduled: 'info',
    published: 'success',
    archived: 'default',
    // Social post statuses
    publishing: 'info',
    failed: 'error',
    // Email statuses
    sent: 'info',
    delivered: 'success',
    opened: 'purple',
    clicked: 'purple',
    bounced: 'error',
    queued: 'warning',
    // Priority
    low: 'default',
    medium: 'warning',
    high: 'error',
    urgent: 'error',
    // Platforms
    linkedin: 'info',
    facebook: 'info',
    instagram: 'accent',
    twitter: 'info',
  };

  return statusMap[status] || 'default';
}

// Platform-specific colors for social media
export function getPlatformStyles(platform: string): string {
  const platformStyles: Record<string, string> = {
    linkedin: 'bg-[#E8F4FD] text-[#0A66C2]',
    facebook: 'bg-[#E7F3FF] text-[#1877F2]',
    instagram: 'bg-[#FFEDF5] text-[#E4405F]',
    twitter: 'bg-[#E8F5FD] text-[#1DA1F2]',
  };
  return platformStyles[platform] || variantStyles.default;
}
